import { CustomField } from 'components/common/form/field/CustomField';
import { FormikField } from 'components/common/form/field/FormikField';
import { CHANNEL } from 'core/agency/AddonFeature';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { connect } from 'formik';
import i18next from 'i18n';
import _ from 'lodash';
import { FC, useCallback } from 'react';
import { getI18nString } from 'utils/I18nUtils';
import styles from './agencyForm.module.scss';
import { normalizeAddonFeatures } from './agencyAddonVisibility';

const AgencySalesChannelAddonForm: FC<any> = (props) => {
  const { addonFeatures } = props.formik.values;
  const setValues = props.formik.setValues;

  const renderChannelAddonSwitch = useCallback(
    (
      channelId: number | undefined,
      defaultMargin: number | undefined,
      needExpressPass: boolean | undefined,
      label: string,
      nameKey: string
    ) => {
      const onChannelAddonChange = (open: boolean) => {
        setValues((values) => {
          // 同步更新 channelMargin（沿用原行為）
          let addonProps = values.addonProps;
          if (channelId) {
            addonProps = {
              ...addonProps,
              channelMargin: {
                ...addonProps.channelMargin,
                [channelId]: open
                  ? defaultMargin !== undefined
                    ? defaultMargin * 100
                    : undefined
                  : undefined
              }
            };
          }
          // channel 關閉時，連動 reset 底下的 ad type / creative 開關
          const nextAddonFeatures = {
            ...values.addonFeatures,
            channel: {
              ...values.addonFeatures.channel,
              [nameKey]: open
            }
          };
          return {
            ...values,
            addonProps,
            addonFeatures: normalizeAddonFeatures(nextAddonFeatures)
          };
        });
      };
      const onChannelExpressChange = (open: boolean) => {
        if (!channelId) {
          return;
        }
        setValues((values) => {
          const expressPass = { ...(values.addonProps.expressPass || {}) };
          if (open) {
            expressPass[channelId] = {
              adDeclarationUrl: _.get(values, 'adDeclaration.url', '')
            };
          } else {
            delete expressPass[channelId];
          }

          return {
            ...values,
            addonProps: {
              ...values.addonProps,
              expressPass
            }
          };
        });
      };
      const useMargin = !_.isNil(defaultMargin) && defaultMargin >= 0;
      return (
        <>
          <FormikField.Switch
            label={label}
            name={`addonFeatures.channel.${nameKey}`}
            onChange={onChannelAddonChange}
          />
          {useMargin && (
            <CustomField label='' fieldContentWidth={370}>
              <FormikField.InputGroup
                label={i18next.t('agency.addonItem.channel.salesChannelMargin')}
                formGroupClassName={`${styles.inputGroup} ${styles.addonPropsField}`}
                fieldContentWidth={240}
                name={`addonProps.channelMargin.${channelId}`}
                type='number'
                step={1}
                disabled={!_.get(addonFeatures, `channel.${nameKey}`)}
                postfix='%'
              />
            </CustomField>
          )}
          {needExpressPass && (
            <CustomField label='' fieldContentWidth={370}>
              <FormikField.Switch
                label={i18next.t(
                  'agency.addonItem.channel.salesChannelExpressPass'
                )}
                formGroupClassName={styles.addonPropsField}
                name={`addonProps.expressPass.${channelId}`}
                fieldContentWidth={240}
                onChange={onChannelExpressChange}
              />
            </CustomField>
          )}
        </>
      );
    },
    [addonFeatures, setValues]
  );

  const renderChannelAddonSwitches = useCallback(() => {
    // ECOPD-5566：新增／編輯經銷商 addon 時，選項清單不得出現停用渠道
    const channelAddonConfigs = ChannelGroupSetting.getActiveAddonConfigs();
    const renderSequence = Object.values(CHANNEL);
    const channelAddonSwitches = Object.keys(channelAddonConfigs)
      .sort((a, b) => {
        // 排序依照 CHANNEL 列表順序，沒有在列表中的放最後
        const aIndex = renderSequence.indexOf(a as CHANNEL);
        const bIndex = renderSequence.indexOf(b as CHANNEL);
        if (aIndex === -1 && bIndex === -1) {
          // 兩者都不在列表中，依數字大小排序
          return Number(a) - Number(b);
        }
        if (aIndex === -1) {
          return 1;
        }
        if (bIndex === -1) {
          return -1;
        }
        return aIndex - bIndex;
      })
      .map((addonKey) => {
        const i18nKey = `agency.addonItem.channel.${addonKey}`;
        const label = getI18nString(
          i18nKey,
          channelAddonConfigs[addonKey].channelName
        );
        const channelAddonConfig = channelAddonConfigs[addonKey];
        if (!channelAddonConfig) {
          return renderChannelAddonSwitch(
            undefined,
            undefined,
            undefined,
            label,
            addonKey
          );
        }
        const { channelId, defaultMargin, needExpressPass } =
          channelAddonConfig;
        return renderChannelAddonSwitch(
          channelId,
          defaultMargin,
          needExpressPass,
          label,
          addonKey
        );
      });
    return [...channelAddonSwitches];
  }, [renderChannelAddonSwitch]);

  return (
    <div className={styles.agencyFrom}>
      <fieldset>
        <legend>
          <span>{i18next.t('agency.addonGroup.channel')}</span>
        </legend>
        <div className={styles.sectionsShadow}>
          <div className={styles.fieldContent}>
            {renderChannelAddonSwitches()}
          </div>
        </div>
      </fieldset>
    </div>
  );
};

export default connect(AgencySalesChannelAddonForm);
